import { useEffect, useState } from "react";
import { bindingLimit, EmailQuota, isAdminError, quotaResumesAt, SendProgress } from "../../webinars/types";
import { webinarRepository } from "../../webinars/repository";
import { formatResetTime, formatTimeUntil } from "../../webinars/format";

interface EmailQuotaPanelProps {
  quota: EmailQuota;
  notify: (message: string) => void;
}

/** From here a meter turns amber: a large send may not fit */
const WARNING_SHARE = 0.8;

const number = (n: number) => n.toLocaleString("en-ZA");

/** One limit in a line: label and figures, then the bar. The month is the main one */
function Meter({ label, used, limit, remaining, main = false }: {
  label: string; used: number; limit: number | null; remaining: number | null; main?: boolean;
}) {
  const share = limit ? Math.min(1, used / limit) : 0;
  const tone = remaining === 0 ? "full" : share >= WARNING_SHARE ? "warning" : "ok";
  return (
    <div className={`wa-quota-meter wa-quota-meter--${main ? "main" : "minor"} wa-quota-meter--${tone}`}>
      <div className="wa-quota-meter-head">
        <span className="wa-quota-meter-label">{label}</span>
        <span className="wa-quota-figures">
          {limit
            ? <><strong>{number(used)}</strong> of {number(limit)} · {number(remaining ?? 0)} left</>
            : <><strong>{number(used)}</strong> sent · no limit set</>}
        </span>
      </div>
      {limit ? (
        <div className="wa-quota-bar" role="meter" aria-label={`Emails sent: ${label.toLowerCase()}`} aria-valuemin={0} aria-valuemax={limit} aria-valuenow={Math.min(used, limit)}>
          <div style={{ width: `${Math.round(share * 100)}%` }} />
        </div>
      ) : null}
    </div>
  );
}

// The email limits: Resend's free plan allows 100 a day (reset at 00:00 UTC = 02:00 SAST) and
// 3,000 a month. Confirmations and joining instructions share both. Emails over a limit wait in
// the queue; after the reset, "Send waiting emails" sends them.
// A compact summary: the figures, any warning and waiting emails always show; reset times and
// the explanation are under Details.
export default function EmailQuotaPanel({ quota, notify }: EmailQuotaPanelProps) {
  const [now, setNow] = useState(Date.now());
  const [sending, setSending] = useState<SendProgress | null>(null);
  const [result, setResult] = useState("");
  const [open, setOpen] = useState(false);

  // Keeps the countdowns current
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const { remaining, waiting } = quota;
  const full = remaining === 0;
  const period = bindingLimit(quota);
  const resumesAt = quotaResumesAt(quota);
  const resumes = `${formatResetTime(resumesAt, now)} (${formatTimeUntil(resumesAt, now)})`;
  const share = (used: number, limit: number | null) => (limit ? (used + quota.inFlight) / limit : 0);
  const dayNearly = share(quota.used, quota.dailyLimit) >= WARNING_SHARE;
  const monthNearly = share(quota.monthUsed, quota.monthlyLimit) >= WARNING_SHARE;
  const nearly = !full && (dayNearly || monthNearly);
  // Name the limit that's nearly used up; if both are, the one that runs out first
  const nearlyMonth = monthNearly && (!dayNearly || period === "month");
  const tone = full ? "full" : nearly ? "warning" : "ok";

  const sendWaiting = async () => {
    setResult("");
    setSending({ queued: 0, sent: 0, failed: 0, remaining: waiting, emailEnabled: true });
    try {
      const done = await webinarRepository.sendWaitingEmails(null, setSending);
      const left = done.quota?.remaining === 0 && done.remaining > 0
        ? ` ${bindingLimit(done.quota) === "month" ? "Monthly" : "Daily"} limit reached: ${done.remaining} still waiting for the reset.`
        : "";
      setResult(!done.emailEnabled
        ? "Email sending isn't switched on yet (no email provider key on the server)."
        : `Sent ${done.sent}${done.failed ? `, ${done.failed} failed` : ""}.${left}`);
      notify("Waiting emails: done");
    } catch (error) {
      setResult(isAdminError(error) ? error.message : "Something went wrong. Nobody gets an email twice, so it's safe to try again.");
    } finally {
      setSending(null);
    }
  };

  return (
    <section className={`wa-panel wa-monitor wa-quota wa-quota--${tone}`} aria-labelledby="wa-quota-title">
      <div className="wa-monitor-head">
        <h2 id="wa-quota-title">Email limits</h2>
        <span className={`wa-monitor-status wa-monitor-status--${tone}`}>
          {full ? "Limit reached" : nearly ? "Nearly used" : "Within limits"}
        </span>
      </div>

      {/* This month's limit first and highlighted; the daily limit underneath */}
      <div className="wa-quota-meters">
        <Meter label="This month" main used={quota.monthUsed} limit={quota.monthlyLimit} remaining={quota.monthRemaining} />
        <Meter label="Today" used={quota.used} limit={quota.dailyLimit} remaining={quota.dayRemaining} />
      </div>

      {full ? (
        <p className="wa-quota-message" role="status">
          <strong>{period === "month" ? "Monthly" : "Daily"} limit reached.</strong> Nothing more can be sent until {resumes}.
          New confirmations and joining instructions wait in the queue until then.
        </p>
      ) : nearly ? (
        <p className="wa-quota-message">
          Nearly at {nearlyMonth ? "this month's" : "today's"} limit: {number((nearlyMonth ? quota.monthRemaining : quota.dayRemaining) ?? 0)} left.
          A large send may not fit, and the rest would wait for the reset.
        </p>
      ) : null}

      {waiting > 0 && (
        <div className="wa-quota-waiting">
          <span>{waiting} email{waiting === 1 ? "" : "s"} waiting to be sent</span>
          <button type="button" className="wa-btn" onClick={sendWaiting} disabled={full || Boolean(sending)}>
            {sending ? `Sending… ${sending.sent + sending.failed}` : "Send waiting emails"}
          </button>
        </div>
      )}
      {result && <p className="wa-muted" role="status">{result}</p>}

      <button type="button" className="wa-link wa-monitor-toggle" aria-expanded={open} aria-controls="wa-quota-details" onClick={() => setOpen(o => !o)}>
        {open ? "Hide details" : "Details"}<span aria-hidden="true">{open ? " ▴" : " ▾"}</span>
      </button>
      {open && (
        <div id="wa-quota-details" className="wa-monitor-details">
          <ul className="wa-quota-resets">
            <li><strong>This month</strong> resets {formatResetTime(quota.monthResetsAt, now)} ({formatTimeUntil(quota.monthResetsAt, now)})</li>
            <li><strong>Today</strong> resets {formatResetTime(quota.resetsAt, now)} ({formatTimeUntil(quota.resetsAt, now)})</li>
          </ul>
          <p className="wa-muted wa-quota-note">
            Registration confirmations and joining instructions share these limits (Resend's free plan: 100 emails a day,
            counted from 02:00 to 02:00 SAST, and {number(3000)} a month). Over a limit, emails wait in the queue rather
            than being lost, and registrations are never refused.
          </p>
        </div>
      )}
    </section>
  );
}
